"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Check, Globe, Users, Calendar, ArrowRight, Shield, Sparkles } from "lucide-react"
import { useTranslations } from "next-intl"
import { Link } from "@/i18n/navigation"
import { SiteHeader } from "@/components/site-header"
import { SiteFooter } from "@/components/site-footer"
import { LocaleSuggestionBanner } from "@/components/locale-suggestion-banner"
import { SectionLabel } from "@/components/section-label"
import { GradientText } from "@/components/gradient-text"
import { AnimatedHeroGraphic } from "@/components/animated-hero-graphic"
import { useEffect } from "react"
import dynamic from "next/dynamic"
import { Motion } from "@/components/motion-wrapper"

// Lazy load heavy components that are below the fold or not immediately needed
const StickyCTA = dynamic(() => import("@/components/sticky-cta").then(mod => ({ default: mod.StickyCTA })), {
  ssr: false, // These don't need SSR
})

const ExitIntentPopup = dynamic(() => import("@/components/exit-intent-popup").then(mod => ({ default: mod.ExitIntentPopup })), {
  ssr: false,
})

const NewsletterSignup = dynamic(() => import("@/components/newsletter-signup").then(mod => ({ default: mod.NewsletterSignup })), {
  ssr: true, // Keep SSR for SEO
})

// Animation variants
const easeOut = [0.16, 1, 0.3, 1] as const

const fadeInUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: easeOut } }
}

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.7, ease: easeOut } }
}

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
}

// Smooth scroll handler
function smoothScrollTo(elementId: string) {
  const element = document.getElementById(elementId)
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" })
  }
}

export default function LandingPage() {
  const t = useTranslations()

  // Add smooth scrolling for anchor links
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const link = target.closest("a[href^='#']")
      if (link) {
        e.preventDefault()
        const href = link.getAttribute("href")
        if (href) {
          smoothScrollTo(href.substring(1))
        }
      }
    }
    document.addEventListener("click", handleClick)
    return () => document.removeEventListener("click", handleClick)
  }, [])

  // Navigation links
  const navigationLinks = [
    { name: t("nav.features"), href: "#features" },
    { name: t("nav.pricing"), href: "#pricing" },
    { name: t("nav.about"), href: "/about" },
    { name: t("nav.contact"), href: "/impressum" },
  ]

  // Features data
  const features = [
    {
      icon: Globe,
      title: t("features.websiteBuilder.title"),
      description: t("features.websiteBuilder.description"),
      badge: t("features.comingSoon"),
    },
    {
      icon: Users,
      title: t("features.membership.title"),
      description: t("features.membership.description"),
      badge: t("features.beta"),
    },
    {
      icon: Calendar,
      title: t("features.modular.title"),
      description: t("features.modular.description"),
      badge: t("features.beta"),
    },
    {
      icon: Sparkles,
      title: t("features.more.title"),
      description: t("features.more.description"),
      isTeaser: true,
    },
  ]

  // Pricing plans
  const pricingPlans = [
    {
      name: t("pricing.plan.name"),
      price: "€5",
      period: t("pricing.plan.period"),
      description: t("pricing.plan.description"),
      features: [
        t("pricing.plan.features.members"),
        t("pricing.plan.features.website"),
        t("pricing.plan.features.management"),
        t("pricing.plan.features.support"),
        t("pricing.plan.features.feedback"),
      ],
      popular: true,
    },
  ]

  // Footer links
  const footerLinks = {
    product: [
      { name: t("footer.links.features"), href: "#features" },
      { name: t("footer.links.pricing"), href: "#pricing" },
    ],
    company: [
      { name: t("footer.links.about"), href: "/about" },
      { name: t("footer.links.contact"), href: "/impressum" },
    ],
    legal: [
      { name: t("footer.links.privacy"), href: "/privacy" },
      { name: t("footer.links.terms"), href: "/terms" },
      { name: t("footer.links.gtc"), href: "/gtc" },
      { name: t("footer.links.betaTerms"), href: "/beta-terms" },
    ],
  }

  // Compliance stats
  const complianceStats = [
    { value: t("compliance.gdpr"), label: t("compliance.gdprLabel") },
    { value: t("compliance.eu"), label: t("compliance.euLabel") },
    { value: t("compliance.hosted"), label: t("compliance.hostedLabel") },
    { value: t("compliance.owned"), label: t("compliance.ownedLabel") },
  ]


  // FAQ data
  const faqKeys = ["betaStatus", "pricing", "clubDataExport", "clubCancellation", "userAccountDeletion", "support", "security", "availability", "features", "cancellation"]
  const faqs = faqKeys.map((key) => ({
    question: t(`faq.items.${key}.question`),
    answer: t(`faq.items.${key}.answer`),
  }))

  // JSON-LD structured data
  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || "https://clubpilot.at"
  
  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "ClubPilot",
    description: t("meta.description"),
    url: baseUrl,
    logo: `${baseUrl}/clubpilot-logo.svg`,
    sameAs: [],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      email: "support@clubpilot.at",
      availableLanguage: ["English", "German"],
    }
  }

  const softwareApplicationData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "ClubPilot",
    description: t("meta.description"),
    url: baseUrl,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: {
      "@type": "Offer",
      price: "5.00",
      priceCurrency: "EUR",
      priceValidUntil: "2026-12-31",
      availability: "https://schema.org/PreOrder",
      description: t("pricing.plan.description"),
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5",
      ratingCount: "1",
      bestRating: "5",
      worstRating: "1",
    },
    featureList: [
      t("features.websiteBuilder.title"),
      t("features.membership.title"),
      t("features.modular.title"),
    ],
    screenshot: `${baseUrl}/clubpilot-logo.svg`,
    softwareVersion: "Beta",
    releaseNotes: "Currently in beta phase. Features may change as we develop the platform.",
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareApplicationData) }}
      />
      <div className="min-h-screen bg-background">
        {/* Skip to content link for accessibility */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-100 focus:px-4 focus:py-2 focus:bg-accent focus:text-accent-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
        >
          {t("common.skipToContent")}
        </a>

        {/* Locale Suggestion Banner */}
        <LocaleSuggestionBanner />

        {/* Header */}
        <SiteHeader navigationLinks={navigationLinks} />

        <main id="main-content">
          {/* Hero Section */}
          <section className="py-28 md:py-44 relative overflow-hidden" aria-labelledby="hero-heading">
            <div className="max-w-6xl px-4 md:px-6 mx-auto">
              <div className="grid md:grid-cols-[1.1fr_0.9fr] gap-12 items-center animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="space-y-8 animate-in fade-in slide-in-from-left duration-700 delay-100">
                  <Badge variant="secondary" className="px-4 py-1">
                    {t("hero.badge")}
                  </Badge>

                  <h1 id="hero-heading" className="text-[2.75rem] md:text-6xl lg:text-[5.25rem] font-normal tracking-tight leading-[1.2]">
                    {t("hero.title")}{" "}
                    <GradientText underline>{t("hero.titleHighlight")}</GradientText>
                    <br />
                    {t("hero.titleLine2")}
                  </h1>

                  <p className="text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
                    {t("hero.description")}
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button size="lg" asChild>
                      <a href="#features">
                        {t("hero.ctaSecondary")}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </a>
                    </Button>
                    <Button variant="outline" size="lg" asChild>
                      <Link href="/waitlist">
                        {t("hero.ctaPrimary")}
                      </Link>
                    </Button>
                  </div>
                </div>

                <div className="hidden md:block relative h-[400px] lg:h-[500px] animate-in fade-in slide-in-from-right duration-700 delay-200">
                  <AnimatedHeroGraphic />
                </div>
              </div>
            </div>
          </section>

          {/* Compliance Section - Inverted */}
          <section className="py-28 md:py-36 bg-foreground text-background dot-pattern relative radial-glow" aria-label="Compliance">
            <div className="max-w-6xl px-4 md:px-6 mx-auto">
              <Motion
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                variants={stagger}
                className="text-center space-y-4 mb-12"
              >
                <Motion variants={fadeInUp}>
                  <div className="inline-flex items-center justify-center mb-4">
                    <div className="w-12 h-12 rounded-xl bg-linear-to-br from-accent to-accent-secondary flex items-center justify-center shadow-accent">
                      <Shield className="h-6 w-6 text-accent-foreground" aria-hidden="true" />
                    </div>
                  </div>
                </Motion>
                <Motion variants={fadeInUp} className="text-2xl md:text-3xl font-normal leading-[1.15]">
                  {t("compliance.title")}
                </Motion>
                <Motion variants={fadeInUp} className="text-base md:text-lg text-background/80 max-w-2xl mx-auto">
                  {t("compliance.description")}
                </Motion>
              </Motion>
              <Motion
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                variants={stagger}
                className="grid grid-cols-2 md:grid-cols-4 gap-8"
              >
                {complianceStats.map((stat, index) => (
                  <Motion key={index} variants={fadeInUp} className="text-center">
                    <div className="text-2xl md:text-3xl font-bold gradient-text mb-2">{stat.value}</div>
                    <div className="text-sm md:text-base text-background/80">{stat.label}</div>
                  </Motion>
                ))}
              </Motion>
            </div>
          </section>

          {/* Features Section */}
          <section id="features" className="py-28 md:py-44 bg-background" aria-labelledby="features-heading">
            <div className="max-w-6xl px-4 md:px-6 mx-auto">
              <Motion
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                variants={stagger}
                className="text-center space-y-4 mb-16"
              >
                <Motion variants={fadeInUp}>
                  <SectionLabel label={t("features.badge")} pulsing />
                </Motion>
                <Motion as="h2" id="features-heading" variants={fadeInUp} className="text-3xl md:text-4xl lg:text-[3.25rem] font-normal leading-[1.15]">
                  {t("features.title")}
                </Motion>
                <Motion as="p" variants={fadeInUp} className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  {t("features.description")}
                </Motion>
              </Motion>

              <Motion
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                variants={stagger}
                className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
              >
                {features.map((feature, index) => (
                  <Motion key={index} variants={fadeInUp}>
                    <Card className={`h-full ${feature.isTeaser ? "border-dashed border-2 opacity-75" : ""}`}>
                      <CardHeader>
                        <div className="flex items-start justify-between mb-4">
                          <div className={`w-14 h-14 rounded-xl bg-linear-to-br from-accent to-accent-secondary flex items-center justify-center shadow-accent ${feature.isTeaser ? "opacity-60" : ""}`}>
                            <feature.icon className="h-7 w-7 text-accent-foreground" aria-hidden="true" />
                          </div>
                          {feature.badge && (
                            <Badge variant="outline" className="text-xs">
                              {feature.badge}
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-xl font-semibold">{feature.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                      </CardContent>
                    </Card>
                  </Motion>
                ))}
              </Motion>
            </div>
          </section>

          {/* Join Beta Section */}
          <section className="py-28 md:py-44 bg-muted" aria-labelledby="beta-heading">
            <div className="max-w-6xl px-4 md:px-6 mx-auto">
              <Motion
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                variants={stagger}
                className="text-center space-y-8"
              >
                <Motion variants={fadeInUp}>
                  <SectionLabel label={t("beta.badge")} pulsing />
                </Motion>
                <Motion as="h2" id="beta-heading" variants={fadeInUp} className="text-3xl md:text-4xl lg:text-[3.25rem] font-normal leading-[1.15]">
                  {t("beta.title")}
                </Motion>
                <Motion as="p" variants={fadeInUp} className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  {t("beta.description")}
                </Motion>
                <Motion variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                  <Button size="lg" asChild>
                    <Link href="/waitlist">
                      {t("beta.cta")}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                </Motion>
              </Motion>
            </div>
          </section>

          {/* Pricing Section */}
          <section id="pricing" className="py-28 md:py-44 bg-background" aria-labelledby="pricing-heading">
            <div className="max-w-6xl px-4 md:px-6 mx-auto">
              <Motion
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                variants={stagger}
                className="text-center space-y-4 mb-16"
              >
                <Motion variants={fadeInUp}>
                  <SectionLabel label={t("pricing.badge")} />
                </Motion>
                <Motion as="h2" id="pricing-heading" variants={fadeInUp} className="text-3xl md:text-4xl lg:text-[3.25rem] font-normal leading-[1.15]">
                  {t("pricing.title")}
                </Motion>
                <Motion as="p" variants={fadeInUp} className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  {t("pricing.description")}
                </Motion>
              </Motion>

              <Motion
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                variants={stagger}
                className="flex justify-center"
              >
                <div className="w-full max-w-md">
                  {pricingPlans.map((plan, index) => (
                    <Motion
                      key={index}
                      variants={fadeInUp}
                    >
                      <Card variant={plan.popular ? "featured" : "default"} className="h-full relative">
                        {plan.popular && (
                          <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-linear-to-r from-accent to-accent-secondary text-accent-foreground border-0">
                            {t("pricing.mostPopular")}
                          </Badge>
                        )}

                        <CardHeader className="text-center">
                          <CardTitle className="text-2xl font-semibold">{plan.name}</CardTitle>
                          <CardDescription>{plan.description}</CardDescription>
                          <div className="pt-4">
                            <span className="text-4xl font-bold">{plan.price}</span>
                            <span className="text-muted-foreground">{plan.period}</span>
                          </div>
                        </CardHeader>

                        <CardContent>
                          <ul className="space-y-3" role="list">
                            {plan.features.map((feature, featureIndex) => (
                              <li key={featureIndex} className="flex items-start">
                                <Check className="h-5 w-5 text-accent mr-3 shrink-0 mt-0.5" aria-hidden="true" />
                                <span className="text-sm text-muted-foreground">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>

                        <CardFooter>
                          <Button
                            className="w-full"
                            variant={plan.popular ? "default" : "outline"}
                            asChild
                          >
                            <Link href="/waitlist">
                              {t("pricing.getStarted")}
                              {plan.popular && <ArrowRight className="ml-2 h-4 w-4" />}
                            </Link>
                          </Button>
                        </CardFooter>
                      </Card>
                    </Motion>
                  ))}
                </div>
              </Motion>
            </div>
          </section>

          {/* FAQ Section */}
          <section id="faq" className="py-28 md:py-44 bg-muted" aria-labelledby="faq-heading">
            <div className="max-w-3xl px-4 md:px-6 mx-auto">
              <Motion
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                variants={stagger}
                className="text-center space-y-4 mb-16"
              >
                <Motion variants={fadeInUp}>
                  <SectionLabel label={t("faq.badge")} />
                </Motion>
                <Motion as="h2" id="faq-heading" variants={fadeInUp} className="text-3xl md:text-4xl lg:text-[3.25rem] font-normal leading-[1.15]">
                  {t("faq.title")}
                </Motion>
                <Motion as="p" variants={fadeInUp} className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  {t("faq.subtitle")}
                </Motion>
              </Motion>

              <Motion
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                variants={fadeIn}
              >
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Motion>
            </div>
          </section>

          {/* Newsletter Section */}
          <section className="py-28 md:py-44 bg-background" aria-labelledby="newsletter-heading">
            <div className="max-w-2xl px-4 md:px-6 mx-auto">
              <Motion
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                variants={fadeInUp}
              >
                <NewsletterSignup />
              </Motion>
            </div>
          </section>

          {/* GDPR Compliance Section */}
          <section className="py-28 md:py-44 bg-background" aria-labelledby="gdpr-heading">
            <div className="max-w-6xl px-4 md:px-6 mx-auto">
              <Motion
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                variants={stagger}
                className="text-center space-y-8"
              >
                <Motion variants={fadeInUp}>
                  <div className="inline-flex items-center justify-center mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-linear-to-br from-accent to-accent-secondary flex items-center justify-center shadow-accent-lg">
                      <Shield className="h-8 w-8 text-accent-foreground" aria-hidden="true" />
                    </div>
                  </div>
                </Motion>
                <Motion as="h2" id="gdpr-heading" variants={fadeInUp} className="text-3xl md:text-4xl lg:text-[3.25rem] font-normal leading-[1.15]">
                  {t("gdpr.title")}
                </Motion>
                <Motion as="p" variants={fadeInUp} className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                  {t("gdpr.description")}
                </Motion>
                <Motion variants={fadeInUp} className="flex flex-wrap items-center justify-center gap-6 pt-4">
                  <div className="flex items-center gap-2 text-accent">
                    <Check className="h-5 w-5" aria-hidden="true" />
                    <span className="font-medium">{t("gdpr.european")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-accent">
                    <Check className="h-5 w-5" aria-hidden="true" />
                    <span className="font-medium">{t("gdpr.rights")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-accent">
                    <Check className="h-5 w-5" aria-hidden="true" />
                    <span className="font-medium">{t("gdpr.transparent")}</span>
                  </div>
                </Motion>
                <Motion variants={fadeInUp} className="pt-4">
                  <Button variant="outline" asChild>
                    <Link href="/privacy">
                      {t("gdpr.learnMore")}
                    </Link>
                  </Button>
                </Motion>
              </Motion>
            </div>
          </section>

          {/* Final CTA Section - Inverted */}
          <section className="py-28 md:py-44 bg-foreground text-background dot-pattern relative radial-glow" aria-labelledby="cta-heading">
            <div className="max-w-6xl px-4 md:px-6 mx-auto">
              <Motion
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                variants={stagger}
                className="text-center space-y-8"
              >
                <Motion as="h2" id="cta-heading" variants={fadeInUp} className="text-3xl md:text-4xl lg:text-[3.25rem] font-normal leading-[1.15]">
                  {t("cta.title")}
                </Motion>
                <Motion as="p" variants={fadeInUp} className="text-lg md:text-xl text-background/80 max-w-2xl mx-auto leading-relaxed">
                  {t("cta.description")}
                </Motion>
                <Motion variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" variant="secondary" className="bg-background text-foreground hover:bg-background/90" asChild>
                    <Link href="/waitlist">
                      {t("cta.primary")}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                </Motion>
              </Motion>
            </div>
          </section>
        </main>

        {/* Footer */}
        <SiteFooter footerLinks={footerLinks} />
      </div>
      <StickyCTA />
      <ExitIntentPopup />
    </>
  )
}
